// src/components/dashboard/mastercard/MastercardDataCompletionForm.tsx
import React, { useState, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { TermsAndConditions } from './TermsAndConditions';
import { COUNTRIES, EMPLOYMENT_TYPES } from '../../../constants/formOptions';
import { getCurrentApiUrl } from '../../../config/server.config';
import '../../../assets/styles/OnboardingForm.module.css';

// Available currencies definition
const AVAILABLE_CURRENCIES = [
  { code: 'EUR', name: 'Euro', symbol: '€' }
  // Other currencies not available at the moment
  // { code: 'USD', name: 'US Dollar', symbol: '$' },
  // { code: 'GBP', name: 'British Pound', symbol: '£' }
];

// Document types for ID selection
const DOCUMENT_TYPES = [
  { value: 'PASSPORT_FRONT', label: 'Passport' },
  { value: 'GOVERNMENT_ID_FRONT', label: 'Government ID Card' },
  { value: 'DRIVERS_LICENCE_FRONT', label: 'Driver\'s License' }
];

interface MastercardDataCompletionFormProps {
  formData: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isLoading: boolean;
}

export function MastercardDataCompletionForm({
  formData,
  onSubmit,
  onCancel,
  isLoading
}: MastercardDataCompletionFormProps) {
  const { isFullOnboarding } = useAuth();
  const isCompleteOnboarding = isFullOnboarding();
  
  // Common fields for both onboarding types
  const [expirationDate, setExpirationDate] = useState<string>('');
  const [selectedCurrency, setSelectedCurrency] = useState<string>(formData.currency || 'EUR');
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [showTerms, setShowTerms] = useState<boolean>(false);
  
  // Fields for fast onboarding only
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>('PASSPORT_FRONT');
  const [documents, setDocuments] = useState<{
    imageFace?: File | null;
    imageFront?: File | null;
    imageBack?: File | null;
  }>({});
  
  // Personal information fields
  const [firstName, setFirstName] = useState<string>(formData.firstName || '');
  const [lastName, setLastName] = useState<string>(formData.lastName || '');
  const [middleName, setMiddleName] = useState<string>('');
  const [dateOfBirth, setDateOfBirth] = useState<string>('');
  const [nationality, setNationality] = useState<string>('');
  const [citizenship, setCitizenship] = useState<string>('');
  const [taxNumber, setTaxNumber] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>(formData.email || '');
  
  // Address fields
  const [addressLine1, setAddressLine1] = useState<string>('');
  const [addressLine2, setAddressLine2] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');
  const [country, setCountry] = useState<string>('ES'); // Default to Spain
  
  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  
  // File input refs
  const imageFaceRef = useRef<HTMLInputElement>(null);
  const imageFrontRef = useRef<HTMLInputElement>(null);
  const imageBackRef = useRef<HTMLInputElement>(null);
  
  // Format date for API with robust parsing
  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Try to parse different formats
    let date: Date;
    
    // Check for DD/MM/YYYY or DD-MM-YYYY format
    if (/^\d{2}[\/\-]\d{2}[\/\-]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[\/\-]/);
      const day = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1; // Month is 0-indexed
      const year = parseInt(parts[2]);
      date = new Date(year, month, day);
    }
    // Check for MM/DD/YYYY or MM-DD-YYYY format (US format)
    else if (/^\d{2}[\/\-]\d{2}[\/\-]\d{4}$/.test(dateString) && parseInt(dateString.substring(0, 2)) > 12) {
      // If first part > 12, it must be day, so DD/MM/YYYY
      const parts = dateString.split(/[\/\-]/);
      const day = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const year = parseInt(parts[2]);
      date = new Date(year, month, day);
    }
    // Default parsing
    else {
      date = new Date(dateString);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date format:', dateString);
      return '';
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };
  
  // Función mejorada para subir documentos usando URLs presignadas
  const uploadDocumentsForFastUser = async (
    apiBaseUrl: string,
    documents: {
      imageFace?: File | null;
      imageFront?: File | null;
      imageBack?: File | null;
    },
    selectedDocumentType: string
  ) => {
    // El token de autenticación
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication token not found');
    }
    
    // Helper function to change user phase to UPLOAD_DOCUMENTS
    const changeToUploadDocumentsPhase = async () => {
      console.log("Changing user phase to UPLOAD_DOCUMENTS");
      
      const phaseResponse = await fetch(`${apiBaseUrl}/onboarding/fast-to-upload-documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!phaseResponse.ok) {
        const errorText = await phaseResponse.text();
        console.error(`Error changing to UPLOAD_DOCUMENTS phase: ${errorText}`);
        throw new Error(`Failed to change phase: ${phaseResponse.status}`);
      }
      
      const phaseData = await phaseResponse.json();
      console.log("Phase change response:", phaseData);
      return phaseData;
    };
    
    // Helper function to revert user phase back to ONBOARDING_COMPLETE_FAST
    const revertToFastCompletePhase = async () => {
      console.log("Reverting user phase to ONBOARDING_COMPLETE_FAST");
      
      const phaseResponse = await fetch(`${apiBaseUrl}/onboarding/revert-to-fast-complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!phaseResponse.ok) {
        const errorText = await phaseResponse.text();
        console.error(`Error reverting to ONBOARDING_COMPLETE_FAST phase: ${errorText}`);
        throw new Error(`Failed to revert phase: ${phaseResponse.status}`);
      }
      
      const phaseData = await phaseResponse.json();
      console.log("Phase revert response:", phaseData);
      return phaseData;
    };
    
    // Nueva función para obtener URL presignada y subir documento
    const uploadDocumentWithPresignedUrl = async (file: File, documentType: string): Promise<string> => {
      console.log(`Uploading ${documentType} document using presigned URL`);
      
      // 1. Obtener URL presignada
      const presignedResponse = await fetch(`${apiBaseUrl}/documents/presigned-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentType,
          fileName: file.name
        })
      });
      
      if (!presignedResponse.ok) {
        const errorText = await presignedResponse.text();
        console.error(`Error getting presigned URL: ${errorText}`);
        throw new Error(`Failed to get presigned URL: ${presignedResponse.status}`);
      }
      
      const presignedData = await presignedResponse.json();
      
      if (!presignedData.success) {
        throw new Error(presignedData.message || 'Failed to get presigned URL');
      }
      
      const { signedUrl, fileName } = presignedData.data;
      
      // 2. Subir archivo directamente a S3 usando URL presignada
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      
      // Crear promesa para seguimiento del progreso y manejo de la carga
      return new Promise<string>((resolve, reject) => {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(prev => ({ ...prev, [documentType]: percentComplete }));
          }
        };
        
        xhr.onload = async () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // 3. Confirmar al servidor que la carga se ha completado
            try {
              const confirmResponse = await fetch(`${apiBaseUrl}/documents/confirm-upload`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  documentType,
                  fileName
                })
              });
      
              if (!confirmResponse.ok) {
                throw new Error(`Failed to confirm upload: ${confirmResponse.status}`);
              }
      
              const confirmData = await confirmResponse.json();
      
              if (confirmData.success) {
                console.log(`Document ${documentType} uploaded and confirmed successfully`);
                resolve(fileName); // Resolver con el nombre del archivo
              } else {
                reject(new Error(`Failed to confirm upload: ${confirmData.message}`));
              }
            } catch (error) {
              reject(error);
            }
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.onabort = () => reject(new Error('Upload aborted'));
        
        // Iniciar la carga
        xhr.send(file);
      });
    };
    
    try {
      // Cambiar fase del usuario a UPLOAD_DOCUMENTS
      await changeToUploadDocumentsPhase();
      
      const uploadedFiles: { [key: string]: string } = {};
      
      // Subir selfie (si existe)
      if (documents.imageFace) {
        toast.info('Uploading selfie photo...');
        uploadedFiles.selfie = await uploadDocumentWithPresignedUrl(documents.imageFace, 'SELFIE');
      }
      
      // Subir documento frontal (siempre requerido)
      if (documents.imageFront) {
        toast.info(`Uploading document front side...`);
        uploadedFiles.front = await uploadDocumentWithPresignedUrl(documents.imageFront, selectedDocumentType);
      }
      
      // Subir documento trasero (solo para documentos que no sean pasaporte)
      if (documents.imageBack && 
          (selectedDocumentType === 'DRIVERS_LICENCE_FRONT' || 
           selectedDocumentType === 'GOVERNMENT_ID_FRONT')) {
        
        // Determinar el tipo de documento trasero
        let backDocumentType = 'PASSPORT_BACK';
        if (selectedDocumentType === 'DRIVERS_LICENCE_FRONT') {
          backDocumentType = 'DRIVERS_LICENCE_BACK';
        } else if (selectedDocumentType === 'GOVERNMENT_ID_FRONT') {
          backDocumentType = 'GOVERNMENT_ID_BACK';
        }
        
        toast.info(`Uploading document back side...`);
        uploadedFiles.back = await uploadDocumentWithPresignedUrl(documents.imageBack, backDocumentType);
      }
      
      // Nota: Se ha eliminado la llamada al endpoint de actualización de tipo de documento en Verestro
      // ya que el endpoint no parece existir y el proceso funciona correctamente sin esta llamada
      
      // Restablecer la fase del usuario a ONBOARDING_COMPLETE_FAST
      await revertToFastCompletePhase();
      
      toast.success('Documents uploaded successfully');
      return uploadedFiles;
      
    } catch (error) {
      console.error('Error during document upload process:', error);
      toast.error('Error uploading documents. Please try again.');
      
      // Intentar restaurar la fase del usuario en caso de error
      try {
        await revertToFastCompletePhase();
      } catch (phaseError) {
        console.error('Error restoring user phase:', phaseError);
      }
      
      throw error;
    } finally {
      // Limpiar el progreso después de un tiempo
      setTimeout(() => {
        setUploadProgress({});
      }, 3000);
    }
  };
  
  const openTermsAndConditions = () => {
    setShowTerms(true);
  };
  
  const handleDocumentTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    setSelectedDocumentType(newType);
    
    // Clear files if document type changes
    setDocuments({});
    
    // Reset file inputs
    if (imageFrontRef.current) imageFrontRef.current.value = '';
    if (imageBackRef.current) imageBackRef.current.value = '';
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: 'imageFace' | 'imageFront' | 'imageBack') => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload JPEG, JPG, PNG or PDF files.');
        e.target.value = '';
        return;
      }
      
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB - actualizado según el límite en documents.routes.ts
      if (file.size > maxSize) {
        toast.error('File is too large. Maximum size is 10MB.');
        e.target.value = '';
        return;
      }
      
      // Update state
      setDocuments(prev => ({
        ...prev,
        [fileType]: file
      }));
    }
  };
  
  const validateCompleteForm = () => {
    // Always validate expiration date and terms
    if (!expirationDate) {
      toast.error('Please enter the document expiration date');
      return false;
    }
    
    // Validate date format
    const formattedDate = formatDateForAPI(expirationDate);
    if (!formattedDate) {
      toast.error('Invalid date format. Please use format: YYYY-MM-DD or DD/MM/YYYY');
      return false;
    }
    
    const expDate = new Date(formattedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
    
    if (expDate <= today) {
      toast.error('Document expiration date must be in the future');
      return false;
    }
    
    if (!termsAccepted) {
      toast.error('You must accept the Terms and Conditions');
      return false;
    }
    
    // These validations only apply for fast onboarding
    if (!isCompleteOnboarding) {
      if (!firstName) {
        toast.error('Please enter your first name');
        return false;
      }
      
      if (!lastName) {
        toast.error('Please enter your last name');
        return false;
      }
      
      if (!dateOfBirth) {
        toast.error('Please enter your date of birth');
        return false;
      }
      
      const birthDate = new Date(dateOfBirth);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (age < 18 || (age === 18 && monthDiff < 0)) {
        toast.error('You must be at least 18 years old');
        return false;
      }
      
      if (!nationality) {
        toast.error('Please select your nationality');
        return false;
      }
      
      if (!citizenship) {
        toast.error('Please select your citizenship');
        return false;
      }
      
      if (!taxNumber) {
        toast.error('Please enter your tax/ID number');
        return false;
      }
      
      if (!phone) {
        toast.error('Please enter your phone number');
        return false;
      }
      
      if (!addressLine1) {
        toast.error('Please enter your address');
        return false;
      }
      
      if (!city) {
        toast.error('Please enter your city');
        return false;
      }
      
      if (!state) {
        toast.error('Please enter your state/province');
        return false;
      }
      
      if (!postalCode) {
        toast.error('Please enter your postal code');
        return false;
      }
      
      if (!country) {
        toast.error('Please select your country');
        return false;
      }
      
      // Document validations
      if (!documents.imageFace) {
        toast.error('Please upload a selfie photo');
        return false;
      }
      
      if (!documents.imageFront) {
        toast.error('Please upload the front of your document');
        return false;
      }
      
      // Only require back image for ID card and driver's license
      if ((selectedDocumentType === 'DRIVERS_LICENCE_FRONT' || 
           selectedDocumentType === 'GOVERNMENT_ID_FRONT') && 
          !documents.imageBack) {
        toast.error('Please upload the back of your document');
        return false;
      }
    }
    
    return true;
  };
  
  const validateSimpleForm = () => {
    // Validate expiration date
    if (!expirationDate) {
      toast.error('Please enter the document expiration date');
      return false;
    }
    
    // Validate date format
    const formattedDate = formatDateForAPI(expirationDate);
    if (!formattedDate) {
      toast.error('Invalid date format. Please use format: YYYY-MM-DD or DD/MM/YYYY');
      return false;
    }
    
    const expDate = new Date(formattedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
    
    if (expDate <= today) {
      toast.error('Document expiration date must be in the future');
      return false;
    }
    
    // Validate terms acceptance
    if (!termsAccepted) {
      toast.error('You must accept the Terms and Conditions');
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate based on onboarding type
    const isValid = isCompleteOnboarding ? validateSimpleForm() : validateCompleteForm();
    if (!isValid) return;
    
    // Get the base API URL (either local or production)
    const apiBaseUrl = getCurrentApiUrl();

    console.log(`Using API base URL: ${apiBaseUrl} (based on hostname: ${window.location.hostname})`);
    
    try {
      if (isCompleteOnboarding) {
        // Simple flow for complete onboarding users
        const formattedExpDate = formatDateForAPI(expirationDate);
        if (!formattedExpDate) {
          toast.error('Invalid date format. Please check the expiration date.');
          return;
        }
        
        const submissionData = {
          documentExpirationDate: formattedExpDate,
          currency: selectedCurrency,
          termsAccepted
        };
        console.log('Sending data to backend:', submissionData);
        await onSubmit(submissionData);
      } else {
        // Complex flow for fast onboarding users
        toast.info("Processing your information and documents...");
        
        try {
          // Step 1: Update personal information
          console.log('Sending personal info update to:', `${apiBaseUrl}/personal-info`);
          const personalDataResponse = await fetch(`${apiBaseUrl}/personal-info`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              firstName: firstName.trim(),
              middleName: middleName.trim() || undefined, // Send undefined instead of empty string or null
              lastName: lastName.trim(),
              dateOfBirth: formatDateForAPI(dateOfBirth),
              nationality,
              citizenship,
              taxNumber: taxNumber.trim().toUpperCase(),
              phone: phone.trim(),
              employmentStatus: "EMPLOYEE",
              employmentDescription: "null",
              employerName: "null",
              occupation: "null"
            })
          });
          
          if (!personalDataResponse.ok) {
            const errorText = await personalDataResponse.text();
            console.error('Personal info update error:', errorText);
            throw new Error(`Failed to update personal information: ${personalDataResponse.status} ${errorText}`);
          }
          
          // Step 2: Update address information
          console.log('Sending address info update to:', `${apiBaseUrl}/address-info`);
          const addressDataResponse = await fetch(`${apiBaseUrl}/address-info`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              addressLine1: addressLine1.trim(),
              addressLine2: addressLine2.trim() || "",
              city: city.trim(),
              state: state.trim(),
              postalCode: postalCode.trim(),
              country
            })
          });
          
          if (!addressDataResponse.ok) {
            const errorText = await addressDataResponse.text();
            console.error('Address info update error:', errorText);
            throw new Error(`Failed to update address information: ${addressDataResponse.status} ${errorText}`);
          }
          
          // Step 3: Upload documents using the new method with presigned URLs
          await uploadDocumentsForFastUser(
            apiBaseUrl,
            documents,
            selectedDocumentType
          );
          
          // Prepare data for Mastercard creation - same as for complete onboarding users
          const formattedExpDate = formatDateForAPI(expirationDate);
          if (!formattedExpDate) {
            toast.error('Invalid date format. Please check the expiration date.');
            return;
          }
          
          const submissionData = {
            documentExpirationDate: formattedExpDate,
            currency: selectedCurrency,
            termsAccepted,
            documentType: selectedDocumentType
          };
          
          // Call onSubmit function to create the Mastercard - same process as complete onboarding
          await onSubmit(submissionData);
          
        } catch (error) {
          console.error('Error submitting form:', error);
          toast.error('An error occurred while processing your request. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while processing your request. Please try again.');
    }
  };

  // Render the form based on onboarding type
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Complete your MasterCard Application</h2>
          <button 
            onClick={onCancel} 
            className="close-button"
            disabled={isLoading}
          >
            &times;
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="form-container">
          {isCompleteOnboarding ? (
            // Simple form for complete onboarding users
            <div className="form-section">
              <h3>Create MasterCard</h3>
              <p>
                Your personal and identity information is already verified in our system.
                Please select your card currency and provide the expiration date of your identification document.
              </p>
            </div>
          ) : (
            // Extended form for fast onboarding users
            <>
              <div className="form-section">
                <h3>Complete Your Information</h3>
                <p>
                  Please provide the required information to create your MasterCard.
                  All fields marked with * are required.
                </p>
              </div>
              
              <div className="form-section">
                <h4>Personal Information</h4>
                
                <div className='flex flex-wrap sm:gap-4'>
                  <div className="form-group sm:flex-1 w-full" >
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>
                  
                   <div className="form-group sm:flex-1 w-full" >
                    <label htmlFor="middleName">Middle Name</label>
                    <input
                      type="text"
                      id="middleName"
                      value={middleName}
                      onChange={(e) => setMiddleName(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  
                   <div className="form-group sm:flex-1 w-full" >
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>
                </div>
                
                <div className='flex sm:gap-4 flex-wrap'>
                   <div className="form-group sm:flex-1 w-full" >
                    <label htmlFor="dateOfBirth">Date of Birth *</label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                      disabled={isLoading}
                      max={new Date(new Date().getFullYear() - 18, new Date().getMonth(), new Date().getDate()).toISOString().split('T')[0]}
                      required
                    />
                    <small>You must be at least 18 years old.</small>
                  </div>
                  
                   <div className="form-group sm:flex-1 w-full" >
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={true} // Email is pre-filled and non-editable
                      required
                      className='w-full'
                    />
                  </div>
                </div>
                
                <div className='flex sm:gap-4 flex-wrap'>
                     <div className="form-group sm:flex-1 w-full" >
                    <label htmlFor="nationality">Nationality *</label>
                    <select
                      id="nationality"
                      value={nationality}
                      onChange={(e) => setNationality(e.target.value)}
                      disabled={isLoading}
                      required
                    >
                      <option value="">Select nationality</option>
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  
                     <div className="form-group sm:flex-1 w-full" >
                    <label htmlFor="citizenship">Citizenship *</label>
                    <select
                      id="citizenship"
                      value={citizenship}
                      onChange={(e) => setCitizenship(e.target.value)}
                      disabled={isLoading}
                      required
                    >
                      <option value="">Select citizenship</option>
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className='flex sm:gap-4 flex-wrap'>
                  <div className="form-group sm:flex-1 w-full" >
                    <label htmlFor="taxNumber">Tax/ID Number *</label>
                    <input
                      type="text"
                      id="taxNumber"
                      value={taxNumber}
                      onChange={(e) => setTaxNumber(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                    <small>Enter your government-issued identification number.</small>
                  </div>
                  
                  <div className="form-group sm:flex-1 w-full" >
                    <label htmlFor="phone">Phone *</label>
                    <input
                      type="tel"
                      id="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={isLoading}
                      required
                       className='w-full'
                    />
                    <small>Enter your phone number with country code (e.g., +34600000000)</small>
                  </div>
                </div>
              </div>
              
              <div className="form-section">
                <h4>Address Information</h4>
                
                <div className="form-group">
                  <label htmlFor="addressLine1">Address Line 1 *</label>
                  <input
                    type="text"
                    id="addressLine1"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="addressLine2">Address Line 2</label>
                  <input
                    type="text"
                    id="addressLine2"
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                
                <div style={{ display: 'flex', gap: '15px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>
                  
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="state">State/Province *</label>
                    <input
                      type="text"
                      id="state"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '15px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="postalCode">Postal Code *</label>
                    <input
                      type="text"
                      id="postalCode"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>
                  
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="country">Country *</label>
                    <select
                      id="country"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      disabled={isLoading}
                      required
                    >
                      <option value="">Select country</option>
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="form-section">
                <h4>Identity Verification</h4>
                
                <div className="form-group">
                  <label htmlFor="documentType">Document Type *</label>
                  <select
                    id="documentType"
                    value={selectedDocumentType}
                    onChange={handleDocumentTypeChange}
                    disabled={isLoading}
                  >
                    {DOCUMENT_TYPES.map(doc => (
                      <option key={doc.value} value={doc.value}>{doc.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="imageFace">Selfie Photo (Front facing camera) *</label>
                  <input
                    type="file"
                    id="imageFace"
                    ref={imageFaceRef}
                    onChange={(e) => handleFileChange(e, 'imageFace')}
                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                    disabled={isLoading}
                  />
                  <small>Upload a clear photo of your face.</small>
                  {uploadProgress['SELFIE'] && (
                    <div className="progress-bar">
                      <div 
                        className="progress" 
                        style={{ width: `${uploadProgress['SELFIE']}%` }}
                      ></div>
                      <span>{uploadProgress['SELFIE']}%</span>
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="imageFront">
                    {selectedDocumentType === 'PASSPORT_FRONT' 
                      ? 'Passport Page with Photo *' 
                      : 'Front Side of Document *'}
                  </label>
                  <input
                    type="file"
                    id="imageFront"
                    ref={imageFrontRef}
                    onChange={(e) => handleFileChange(e, 'imageFront')}
                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                    disabled={isLoading}
                  />
                  <small>Make sure all details are clearly visible.</small>
                  {uploadProgress[selectedDocumentType] && (
                    <div className="progress-bar">
                      <div 
                        className="progress" 
                        style={{ width: `${uploadProgress[selectedDocumentType]}%` }}
                      ></div>
                      <span>{uploadProgress[selectedDocumentType]}%</span>
                    </div>
                  )}
                </div>
                
                {/* Back side only required for non-passport documents */}
                {selectedDocumentType !== 'PASSPORT_FRONT' && (
                  <div className="form-group">
                    <label htmlFor="imageBack">Back Side of Document *</label>
                    <input
                      type="file"
                      id="imageBack"
                      ref={imageBackRef}
                      onChange={(e) => handleFileChange(e, 'imageBack')}
                      accept="image/jpeg,image/jpg,image/png,application/pdf"
                      disabled={isLoading}
                    />
                    <small>Make sure all details are clearly visible.</small>
                    {uploadProgress[selectedDocumentType.replace('FRONT', 'BACK')] && (
                      <div className="progress-bar">
                        <div 
                          className="progress" 
                          style={{ width: `${uploadProgress[selectedDocumentType.replace('FRONT', 'BACK')]}%` }}
                        ></div>
                        <span>{uploadProgress[selectedDocumentType.replace('FRONT', 'BACK')]}%</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          
          {/* Common fields for both onboarding types */}
          <div className="form-group">
            <label htmlFor="expirationDate">Document Expiration Date *</label>
            <input
              type="date"
              id="expirationDate"
              value={expirationDate}
              onChange={(e) => {
                setExpirationDate(e.target.value);
                // Validate immediately on change
                if (e.target.value) {
                  const formatted = formatDateForAPI(e.target.value);
                  if (!formatted) {
                    toast.warning('Please enter date in format YYYY-MM-DD');
                  }
                }
              }}
              onBlur={(e) => {
                // Additional validation on blur
                if (e.target.value) {
                  const formatted = formatDateForAPI(e.target.value);
                  if (!formatted) {
                    toast.error('Invalid date format. Please use YYYY-MM-DD');
                  }
                }
              }}
              disabled={isLoading}
              min={new Date().toISOString().split('T')[0]} // Minimum date is today
              required
              placeholder="YYYY-MM-DD"
              pattern="\d{4}-\d{2}-\d{2}"
            />
            <small>Your document must be valid (not expired). Format: YYYY-MM-DD or DD/MM/YYYY</small>
          </div>
          
          <div className="form-group">
            <label htmlFor="currency">Card Currency *</label>
            <select 
              id="currency"
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              disabled={isLoading}
              required
            >
              {AVAILABLE_CURRENCIES.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.name} ({currency.symbol})
                </option>
              ))}
            </select>
            <small>Select the currency for your MasterCard.</small>
          </div>
          
          <div className="form-group terms-container">
            <div className="terms-checkbox">
              <input
                type="checkbox"
                id="termsAccepted"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={isLoading}
                required
              />
              <label htmlFor="termsAccepted">
                I accept the <button 
                  type="button" 
                  onClick={openTermsAndConditions} 
                  className="terms-link"
                >
                  Terms and Conditions
                </button>
              </label>
            </div>
          </div>
          
          <div className="form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="cancel-button"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Create MasterCard'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Terms and Conditions Modal */}
      {showTerms && (
        <TermsAndConditions onClose={() => setShowTerms(false)} />
      )}
    </div>
  );
}